import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { track } from "@/lib/track";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck, Scales, CheckCircle, CircleNotch, ArrowRight } from "@phosphor-icons/react";

const SEEN_KEY = "tc_exit_popup_seen_at";
const SUBSCRIBED_KEY = "tc_newsletter_subscribed";
const RESHOW_DAYS = 7;

const VARIANTS = {
  scam: {
    tag: "Free checklist",
    Icon: ShieldCheck,
    heading: "Before you go — grab our free Scam Tipster Checklist",
    sub: "Join the newsletter and get our 12-point checklist to spot a dodgy tipster in seconds — plus new verified reviews and Trust Score movers. No spam, unsubscribe anytime.",
    button: "Send me the checklist",
    linkText: "Open the Scam Tipster Checklist",
    linkTo: "/tipster-scam-checklist",
  },
  comparison: {
    tag: "Free cheat sheet",
    Icon: Scales,
    heading: "Stuck between two tipsters? Get our free Comparison Cheat Sheet",
    sub: "Join the newsletter and get our cheat sheet on exactly how to compare two tipsters — the metrics that matter and the questions to ask. No spam, unsubscribe anytime.",
    button: "Send me the cheat sheet",
    linkText: "Open the Comparison Cheat Sheet",
    linkTo: "/tipster-comparison-cheat-sheet",
  },
};

export function ExitIntentPopup() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const armed = useRef(false);
  const variantRef = useRef("scam");

  useEffect(() => {
    let skip = false;
    try {
      if (localStorage.getItem(SUBSCRIBED_KEY) === "1") {
        skip = true; // subscribers never see it again
      } else {
        const seenAt = parseInt(localStorage.getItem(SEEN_KEY) || "0", 10);
        // re-show only after RESHOW_DAYS have passed since they last saw it
        if (seenAt && Date.now() - seenAt < RESHOW_DAYS * 86400000) skip = true;
      }
    } catch (e) { /* private mode — treat as not seen */ }
    if (skip) return;

    const trigger = () => {
      if (armed.current) return;
      armed.current = true;
      try { localStorage.setItem(SEEN_KEY, String(Date.now())); } catch (e) { /* ignore */ }
      variantRef.current = window.location.pathname.startsWith("/compare") ? "comparison" : "scam";
      setOpen(true);
      track("newsletter_popup_viewed", { variant: variantRef.current, path: window.location.pathname });
      cleanup();
    };
    const onMouseOut = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget) trigger();
    };
    // Desktop: exit intent. Mobile/all: fallback timer so it still converts.
    const timer = setTimeout(trigger, 50000);
    document.addEventListener("mouseout", onMouseOut);
    function cleanup() {
      clearTimeout(timer);
      document.removeEventListener("mouseout", onMouseOut);
    }
    return cleanup;
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/newsletter/subscribe", { email, name: null });
      try { localStorage.setItem(SUBSCRIBED_KEY, "1"); } catch (err) { /* ignore */ }
      track("newsletter_popup_subscribed", { variant: variantRef.current, source: "exit_popup" });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const v = VARIANTS[variantRef.current] || VARIANTS.scam;
  const Icon = v.Icon;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md overflow-hidden p-0" data-testid="exit-intent-popup">
        <div className="bg-[#0b1220] px-6 pb-6 pt-8 text-white">
          <div className="flex items-center gap-2 text-primary">
            <Icon size={22} weight="fill" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em]">{v.tag}</span>
          </div>

          {done ? (
            <div className="mt-4" data-testid="exit-intent-success">
              <div className="flex items-center gap-2 text-emerald-400">
                <CheckCircle size={26} weight="fill" />
                <h2 className="font-heading text-xl font-bold">You&rsquo;re in — here it is</h2>
              </div>
              <p className="mt-2 text-sm text-white/70">
                Check your inbox to confirm your subscription (your free guide is on its way). In the meantime, here it is:
              </p>
              <Button asChild className="mt-4 w-full rounded-md font-semibold" data-testid="exit-intent-checklist-link">
                <Link to={v.linkTo} onClick={() => setOpen(false)}>
                  {v.linkText} <ArrowRight size={16} className="ml-1.5" />
                </Link>
              </Button>
            </div>
          ) : (
            <>
              <h2 className="mt-3 font-heading text-2xl font-bold leading-tight">{v.heading}</h2>
              <p className="mt-2 text-sm text-white/70">{v.sub}</p>
              <form onSubmit={submit} className="mt-4 space-y-3">
                <Input
                  type="email"
                  required
                  placeholder="you@example.com"
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  className="h-12 border-white/15 bg-white/10 text-white placeholder:text-white/50"
                  data-testid="exit-intent-email"
                />
                <Button type="submit" disabled={loading} className="h-12 w-full rounded-md font-semibold" data-testid="exit-intent-submit">
                  {loading ? <CircleNotch size={18} className="animate-spin" /> : v.button}
                </Button>
                {error && <p className="text-sm text-red-400" data-testid="exit-intent-error">{error}</p>}
              </form>
              <button
                type="button"
                onClick={() => { track("newsletter_popup_dismissed", { variant: variantRef.current }); setOpen(false); }}
                className="mt-3 w-full text-center text-xs text-white/50 hover:text-white/80"
                data-testid="exit-intent-dismiss"
              >
                No thanks, I&rsquo;ll risk it
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

